/**
 * Lesson-save guard for scenario groups (Phase 5d).
 *
 * Called from `courseService.saveLessonContent` before persisting. Validates:
 *  - Every `slide.scenarioGroupId` references a `content.scenarioGroups[].id`
 *  - Every scenarioGroups entry has ≥1 scenario-template slide bound to it
 *  - Bundle groups (≥2 slides) additionally pass `validateScenarioSequence`
 *  - No group exceeds the 3-slide compositor maximum
 *
 * Single-template experiences (1 scenario slide in a group) are valid by
 * design per CREATE-DIRECTION-NOTE.md — they're a first-class shape
 * alongside multi-slide bundles. Sequence rules only apply when ≥2 slides
 * are present.
 *
 * Throws a structured error on failure so the UI can surface specifics.
 * Non-grouped lessons (no scenarioGroups, no scenarioGroupId bindings) are a
 * no-op — every existing manually-authored lesson keeps saving unchanged.
 */

import type { SlideBasedContent } from '../../../components/course-builder/slides/slideTypes';
import { validateScenarioSequence } from './validateSequence';
import { isScenarioTemplateId } from './templateRegistry';

export class ScenarioGroupValidationError extends Error {
  readonly errors: string[];

  constructor(errors: string[]) {
    super(`Scenario group validation failed: ${errors.join('; ')}`);
    this.name = 'ScenarioGroupValidationError';
    this.errors = errors;
  }
}

/**
 * A slide template that counts as a group *member* for the membership rules
 * (≥1 slide, ≤3 slides, repair's residue detection). Wider than the sequence
 * registry: the live Create pipeline binds two shapes to groups that the
 * compositor registry doesn't know — a Studio compose run pins
 * 'composed-experience' (CreateRoom wraps it in a single-slide group) and the
 * Library path can build 'hotspot-explorer'. Counting only registry ids made
 * both look like empty residue: the validator wedged those lessons, and the
 * repair would have deleted their groups outright. Sequence rules stay
 * registry-only — they're pairing metadata the extra shapes don't have.
 */
function isScenarioMemberTemplate(t: unknown): t is string {
  return (
    typeof t === 'string' &&
    (isScenarioTemplateId(t) || t === 'composed-experience' || t === 'hotspot-explorer')
  );
}

/**
 * Repair the two *residue* shapes before validation:
 *  - a slide bound to a scenarioGroupId with no matching scenarioGroups entry
 *    (the group was removed; the binding is unrenderable residue)
 *  - a scenarioGroups entry with zero scenario-template member slides
 *    (the slides were deleted; the entry is invisible residue — and, because
 *    the save gate hard-blocks on it, it used to wedge the whole lesson:
 *    every save failed, including the edit that would have fixed it)
 *
 * Residue is not authored content — it renders as nothing and only exists to
 * fail validation — so stripping it with a breadcrumb beats blocking the
 * author's unrelated edits behind an error they cannot see or fix. Real
 * violations (bad sequences, >3-slide groups) are NOT repaired; they still
 * throw so genuinely malformed experiences never reach students.
 *
 * Pure. Returns the original object untouched when there is nothing to
 * repair, so callers can cheaply detect the no-op case.
 */
export function repairLessonContent(content: SlideBasedContent): {
  content: SlideBasedContent;
  repairs: string[];
} {
  const repairs: string[] = [];
  const groups = content.scenarioGroups ?? [];
  const groupIds = new Set(groups.map((g) => g.id));

  // Groups with zero scenario-template member slides are residue.
  const templateMemberCount = new Map<string, number>();
  for (const s of content.slides) {
    if (!s.scenarioGroupId) continue;
    const t = s.data?.template;
    if (isScenarioMemberTemplate(t)) {
      templateMemberCount.set(
        s.scenarioGroupId,
        (templateMemberCount.get(s.scenarioGroupId) ?? 0) + 1,
      );
    }
  }
  const emptyGroupIds = new Set(
    groups.filter((g) => (templateMemberCount.get(g.id) ?? 0) === 0).map((g) => g.id),
  );

  // A binding is residue when its group doesn't exist OR is being removed as
  // empty (unbinding the empty group's non-template members — e.g. stray
  // bridges — keeps rule 1 satisfied after the entry goes).
  const strippedBindings: string[] = [];
  const slides = content.slides.map((s) => {
    if (!s.scenarioGroupId) return s;
    if (groupIds.has(s.scenarioGroupId) && !emptyGroupIds.has(s.scenarioGroupId)) return s;
    strippedBindings.push(s.id);
    const { scenarioGroupId: _dropped, ...rest } = s;
    return rest as typeof s;
  });
  if (strippedBindings.length > 0) {
    repairs.push(
      `Unbound ${strippedBindings.length} slide(s) from missing/empty scenario groups (${strippedBindings.join(', ')})`,
    );
  }
  for (const id of emptyGroupIds) {
    repairs.push(`Removed empty scenario group "${id}" (no scenario slides remained)`);
  }

  if (repairs.length === 0) return { content, repairs };

  const keptGroups = groups.filter((g) => !emptyGroupIds.has(g.id));
  return {
    content: {
      ...content,
      slides,
      // Preserve the "no groups" shape rather than materialising an empty array.
      ...(content.scenarioGroups !== undefined ? { scenarioGroups: keptGroups } : {}),
    },
    repairs,
  };
}

export function validateLessonContent(content: SlideBasedContent): void {
  const errors: string[] = [];
  const groups = content.scenarioGroups ?? [];
  const slidesByGroup = new Map<string, typeof content.slides>();

  // Bucket slides by their scenarioGroupId
  for (const slide of content.slides) {
    if (!slide.scenarioGroupId) continue;
    const bucket = slidesByGroup.get(slide.scenarioGroupId) ?? [];
    bucket.push(slide);
    slidesByGroup.set(slide.scenarioGroupId, bucket);
  }

  // 1. Every binding must reference a real group
  const groupIds = new Set(groups.map((g) => g.id));
  for (const slideGroupId of slidesByGroup.keys()) {
    if (!groupIds.has(slideGroupId)) {
      errors.push(
        `Slide references scenarioGroupId "${slideGroupId}" but no matching scenarioGroups entry exists`,
      );
    }
  }

  // 2. Every declared group must have ≥1 scenario slide; sequence must validate
  for (const group of groups) {
    const memberSlides = (slidesByGroup.get(group.id) ?? []).slice().sort((a, b) => a.order - b.order);
    // Filter to scenario members (drop bridges, drop unknown types)
    const memberTypes: string[] = memberSlides
      .map((s) => s.data?.template)
      .filter(isScenarioMemberTemplate);

    if (memberTypes.length < 1) {
      errors.push(
        `Scenario group "${group.id}" must contain at least 1 scenario slide (found ${memberTypes.length})`,
      );
      continue;
    }

    if (memberTypes.length > 3) {
      errors.push(
        `Scenario group "${group.id}" exceeds the 3-slide maximum (found ${memberTypes.length})`,
      );
      continue;
    }

    // Sequence rules (opener/core/closer ordering, combo constraints) only
    // apply to bundles of *registry* templates — composed-experience and
    // hotspot-explorer carry no position/combo metadata, so they count for
    // membership above but are skipped here.
    const scenarioTypes = memberTypes.filter((t) => isScenarioTemplateId(t));
    if (scenarioTypes.length >= 2) {
      const result = validateScenarioSequence(scenarioTypes);
      if (!result.valid) {
        for (const e of result.errors) {
          errors.push(`Scenario group "${group.id}": ${e}`);
        }
      }
    }
  }

  if (errors.length > 0) {
    throw new ScenarioGroupValidationError(errors);
  }
}
